import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Input from "components/DesignSystem/Input/Input";
import Pencil from "components/icons/pencil";
import { InvoiceCustomerModal } from "components/InvoiceCustomerModal/InvoiceCustomerModal";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { Field, FieldProps, useFormikContext } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ReactNode, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { components, MultiValue, OptionProps, SingleValue } from "react-select";
import {
  useGetAllInvoiceCustomersQuery,
  useGetCurrenciesQuery,
  useGetEntityInvoiceQuery,
  useGetInvoiceSettingsQuery,
  useUpdateInvoiceMutation,
} from "store/apis/invoices";
import { FormikListener } from "pages/Books/Invoicing/AddInvoice";
import { useQuery } from "hooks/useQuery";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import ToolTip from "components/design/toolTip";
import { InvoiceFormSection } from "components/InvoiceForm/InvoiceFormSection";
import { InvoiceDiscountFields } from "components/InvoiceForm/InvoiceDiscountFields";
import { InvoiceAdditionalDetails } from "components/InvoiceForm/InvoiceAdditionalDetails";
import { DueDate } from "components/design/DueDate";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { Divider } from "components/design/Divider";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { formatDate } from "utils/formatDate";
import { formatNumber } from "utils/formatNumber";
import { USD } from "constants/currency";
import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import dayjs from "dayjs";
import { AddLineItemButton } from "components/LineItems/AddLineItemButton";
import { LineItemTable } from "components/LineItems/LineItemTable";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { FOCUS_BLOCK_PLACEMENT } from "constants/focusBlock";
import { LineItemUI } from "components/LineItems/LineItemUI";
import { InvoiceSalesTaxCompliance } from "components/SalesTax/InvoiceSalesTaxCompliance";
import {
  LIABILITY_CATEGORY_IDENTIFIER,
  REVENUE_CATEGORY_IDENTIFIER,
} from "constants/categoryIdentifier";
import FocusBlock from "components/DesignSystem/FocusBlock/FocusBlock";
import { Invoice, InvoiceItem } from "types/Models/invoice";
import { ExchangeRateContent } from "components/CustomExchangeRate/CustomExchangeRateFocusBlock";
import { Info } from "components/icons/Info";
import { colors } from "constants/colors";
import { Tag } from "components/DesignSystem/Tag/Tag";
import classNames from "classnames";
import {
  useGetAllCreditNotesQuery,
  useUnlinkCreditNoteMutation,
} from "store/apis/creditNotes";
import { CREDIT_NOTE_STATUS } from "constants/creditNotes";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { CreditNotesTable } from "components/InvoiceInfo/InvoiceInfo";
import { InvoiceAttachments } from "components/InvoiceForm/InvoiceAttachments";
import { debounce } from "utils/debouncing";

export const Label = ({
  children,
  info,
}: {
  children: ReactNode;
  info?: ReactNode;
}) => {
  return (
    <div className="t-flex t-justify-between t-items-center t-w-full">
      <div className="t-text-text-100 t-text-body-sm">{children}</div>
      {info && <div className="t-text-text-30 t-text-body-sm">{info}</div>}
    </div>
  );
};

const SelectOrEditCustomer = ({
  children,
  ...props
}: OptionProps<OptionData>) => {
  return (
    <components.Option
      {...props}
      className="[&.select\_\_option--is-focused]:t-bg-surface-lighter-grey [&.select\_\_option--is-selected]:t-bg-i-surface-light-purple"
    >
      <div className="t-flex t-gap-2.5 t-font-medium t-items-center t-justify-between t-px-3 ">
        <div className="t-cursor-pointer t-truncate !t-border-none t-py-2.5">
          {children}
        </div>
        <Button
          customType="ghost_icon"
          size="small"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            // @ts-ignore
            props.selectProps?.onEditCustomer?.(props.data.value);
          }}
        >
          <Pencil color="currentColor" size="14" strokeWidth="1.5" />
        </Button>
      </div>
    </components.Option>
  );
};

export const invoiceInitialValues = {
  title: "",
  invoice_number: "",
  invoice_number_without_prefix: "",
  po_number: "",
  po_issue_date: "" as string | null,
  sent_to: "",
  sent_on: "",
  invoice_amount: "",
  due_amount: "",
  discount: "",
  discount_type: "PERCENT",
  invoice_date: "" as string | null,
  due_date: "" as string | null,
  sub_total: "",
  tax: "",
  tax_type: "PERCENT",
  total: "",
  paid_amount: "",
  due_balance: "",
  notes: "",
  payment_instructions: "",
  terms: "",
  footer: "",
  customer_id: "",
  created_on: "",
  invoice_items: [] as InvoiceItem[],
  currency: "USD",
};

export type InvoiceItemType = InvoiceItem & { is_new_item: boolean };

export const InvoiceForm = ({
  onChange,
  groupId,
}: {
  onChange: (invoice: Partial<Invoice>) => void;
  groupId: string;
}) => {
  const [customerId, setCustomerId] = useState<string | null | "NEW">("");

  const currencyModal = useModal();

  const [newCurrency, setNewCurrency] = useState<string | null>();
  const [overlayActiveItem, setOverlayActiveItem] = useState<string | null>(
    null
  );
  const [invoiceItem, setInvoiceItem] = useState<InvoiceItemType | null>(null);
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const group = useCurrentGroupContext();
  const query = useQuery();
  const editFlow = query.get("editflow");
  const entityId = useCurrentEntityId();

  const { values, setFieldValue, errors } =
    useFormikContext<typeof invoiceInitialValues>();
  const [updateInvoice, { isLoading: isUpdating }] = useUpdateInvoiceMutation();
  const { alertToast } = useToast();

  const { data: invoice } = useGetEntityInvoiceQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      invoiceId,
    },
    { skip: !group?.uuid || !entityId }
  );
  const isSentInvoiceEditFlow =
    (invoice?.status === "DUE" || invoice?.status === "OVERDUE") &&
    Boolean(editFlow);

  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>();

  const { data: invoiceCustomersData, isFetching: isFetchingCustomers } =
    useGetAllInvoiceCustomersQuery(
      {
        groupId: group?.uuid!,
        entityId: entityId!,
        searchTerm: customerSearchTerm,
      },
      { skip: !group?.uuid || !entityId }
    );
  const invoiceCustomers = invoiceCustomersData?.recipients;

  const onCustomerSearch = useMemo(
    () =>
      debounce((value: string) => setCustomerSearchTerm(value || undefined)),
    []
  );

  const { data: selectedCustomerData } = useGetAllInvoiceCustomersQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      customerId: values.customer_id,
    },
    { skip: !group?.uuid || !entityId || !values.customer_id }
  );

  const customer = selectedCustomerData?.recipients?.[0];

  const { data: invoiceSettings } = useGetInvoiceSettingsQuery(
    { groupId: group?.uuid!, entityId },
    { skip: !group?.uuid || !entityId }
  );

  const { data: currenciesResponse } = useGetCurrenciesQuery();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];

    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const handleUpdate = async (invoice_items: InvoiceItem[]) => {
    if (isSentInvoiceEditFlow) {
      const updatedItems = invoice_items.map((item) => ({
        ...item,
        amount: String((item.rate || 0) * (item.quantity || 0)),
      }));
      setFieldValue("invoice_items", updatedItems);
    }
    if (groupId && entityId && !isSentInvoiceEditFlow) {
      const itemsPayload = invoice_items.map(({ tax_rate, ...item }) => ({
        ...item,
        tax_rate_id: item.tax_rate_id || tax_rate?.uuid || null,
      }));
      try {
        const response = await updateInvoice({
          payload: { invoice_items: itemsPayload },
          groupId: groupId,
          entityId: entityId,
          invoiceId,
        }).unwrap();
        setFieldValue("invoice_items", response.invoice_items);
      } catch (error) {
        alertToast(
          { message: (error as BackendError).data?.error?.message },
          error as Error
        );
      }
    }
  };

  const dueDate =
    values.due_date || (isSentInvoiceEditFlow ? null : invoice?.due_date);

  const currentCurrency = currencyOptions.find(
    (currency) => currency.value === values.currency
  );

  const currency = currentCurrency?.value;
  const invoiceDate = values.invoice_date || invoice?.invoice_date;

  const dates = useMemo(
    () => ({
      INVOICE_DATE: invoiceDate ? dayjs(invoiceDate) : null,
      NET_30: dayjs(invoiceDate).add(30, "days"),
      NET_60: dayjs(invoiceDate).add(60, "days"),
      NET_90: dayjs(invoiceDate).add(90, "days"),
    }),
    [invoiceDate]
  );

  const options = useMemo(() => {
    return [
      ...(invoiceDate
        ? [
            {
              label: <Label info="Due on receipt">Invoice date</Label>,
              value: dayjs(dates.INVOICE_DATE).format(YYYY_MM_DD),
            },
          ]
        : []),
      {
        label: <Label info="Net 30">{formatDate(dates.NET_30)}</Label>,
        value: dayjs(dates.NET_30).format(YYYY_MM_DD),
      },
      {
        label: <Label info="Net 60">{formatDate(dates.NET_60)}</Label>,
        value: dayjs(dates.NET_60).format(YYYY_MM_DD),
      },
      {
        label: <Label info="Net 90">{formatDate(dates.NET_90)}</Label>,
        value: dayjs(dates.NET_90).format(YYYY_MM_DD),
      },
      {
        label: (
          <Label info={dueDate ? "Custom" : ""}>
            {dueDate ? formatDate(dueDate) : "Custom"}
          </Label>
        ),
        value: "CUSTOM",
      },
    ];
  }, [dates, dueDate]);

  const invoiceItems = isSentInvoiceEditFlow
    ? values.invoice_items
    : invoice?.invoice_items;

  const handleItemDelete = (index: number) => {
    const updatedItems = invoiceItems?.filter((item, idx) => idx !== index);
    handleUpdate(updatedItems || []);
  };

  const handleItemEdit = (item: InvoiceItem) => {
    setInvoiceItem({ ...item, is_new_item: false });
  };

  const handleItemDuplicate = (item: InvoiceItem) => {
    setInvoiceItem({ ...item, uuid: "", is_new_item: true });
    setOverlayActiveItem("add-line-item");
  };

  const handleCloseEdit = () => {
    setInvoiceItem(null);
    setOverlayActiveItem(null);
  };

  const handleSaveItem = async (item: InvoiceItemType, index?: number) => {
    try {
      if (!item.is_new_item) {
        const updatedItems = invoiceItems?.map((existingItem, idx) =>
          idx === index ? item : existingItem
        );
        await handleUpdate(updatedItems || []);
      } else {
        await handleUpdate([...(invoiceItems || []), item]);
      }
    } finally {
      handleCloseEdit();
    }
  };

  const isFutureInvoiceDate =
    values.invoice_date && dayjs(values.invoice_date).isAfter(dayjs());

  const isUpdatedRate = invoice?.has_exchange_rate_changed;

  const exchangeRate = invoice?.payment_record_amount?.exchange_rate || 0;

  const originalRate = invoice?.payment_record_amount?.original_exchange_rate
    ? invoice?.payment_record_amount?.original_exchange_rate
    : exchangeRate;

  const exchangeRateBannerDescription = isUpdatedRate
    ? `Exchange Rate: 1.00 USD = ${
        exchangeRate ? formatNumber(Number(exchangeRate)) : "..."
      } ${values.currency}`
    : `Exchange Rate (as of ${formatDate(
        isFutureInvoiceDate ? dayjs().toISOString() : values.invoice_date
      )}): 1.00 USD = ${
        exchangeRate ? formatNumber(Number(exchangeRate)) : "..."
      } ${values.currency}`;

  const selectedCustomerOption = values.customer_id
    ? {
        label:
          invoiceCustomers?.find((c) => c.customer_id === values.customer_id)
            ?.company_name ||
          customer?.company_name ||
          "",
        value: values.customer_id,
      }
    : null;

  const customerOptions = invoiceCustomers?.map((v) => ({
    label: v.company_name,
    value: v.customer_id,
  }));

  const hasCreditNotes =
    isSentInvoiceEditFlow &&
    (invoice?.linked_credit_debit_notes?.length ?? 0) > 0;

  const handleCustomerChange = (
    value: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (value instanceof Array) return;

    const currentCustomer = invoiceCustomers?.find(
      (customer) => customer.customer_id === value?.value
    );
    setFieldValue("currency", currentCustomer?.currency);
    setFieldValue("customer", currentCustomer);
  };

  const handleCurrencyChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (option instanceof Array || !option) return;
    if (values.currency === option.value) return;

    setNewCurrency(option.value);
    currencyModal.open();
  };

  const handleInvoiceNumberBlur = () => {
    if (errors?.invoice_number) {
      setFieldValue("invoice_number", invoice?.invoice_number);
    }
  };

  const handleAddLineItem = () => {
    setInvoiceItem({
      description: "",
      quantity: 1,
      rate: 0.0,
      uuid: "",
      product_data_id: null,
      billing_period_enabled: false,
      billing_period_start_date: invoiceDate,
      billing_period_end_date: null,
      category_id: null,
      is_new_item: true,
    });
  };

  return (
    <div>
      <FocusBlock.Root
        value={overlayActiveItem}
        onValueChange={setOverlayActiveItem}
      >
        <div className="t-flex t-flex-col t-gap-12 t-pb-10">
          <FormikListener values={values} callback={onChange} />
          <div className="t-flex t-flex-col t-gap-4">
            <Combobox
              label="Select customer"
              required
              isDisabled={isSentInvoiceEditFlow}
              isLoading={isFetchingCustomers}
              options={customerOptions}
              onInputChange={onCustomerSearch}
              filterOption={null}
              withForm
              onChange={handleCustomerChange}
              value={selectedCustomerOption}
              defaultValue={
                invoice?.customer && {
                  label: invoice.customer.company_name,
                  value: invoice.customer.uuid!,
                }
              }
              size="regular"
              name="customer_id"
              placeholder="Select customer"
              actions={
                <Button
                  type="button"
                  onClick={() => setCustomerId("NEW")}
                  customType="link"
                  size="small"
                >
                  Add customer
                </Button>
              }
              components={{
                Option: SelectOrEditCustomer,
                NoOptionsMessage: () => (
                  <p className="t-m-0 t-text-center t-py-4">
                    No customers added
                  </p>
                ),
              }}
              // @ts-ignore
              onEditCustomer={setCustomerId}
            />
            {group?.uuid && (
              <InvoiceSalesTaxCompliance
                groupId={group.uuid}
                entityId={entityId}
                buyerStateName={customer?.billing_address?.state}
              />
            )}
          </div>

          <InvoiceFormSection title="Invoice details">
            <Input
              withForm
              name="title"
              label="Invoice title"
              placeholder="Enter invoice title"
            />
            <div className="t-grid t-grid-cols-2 t-gap-x-3 t-gap-y-4 sm:t-grid-cols-1">
              <Input
                withForm
                name="invoice_number"
                label="Invoice number"
                placeholder="Enter invoice number"
                required
                showErrorOnceTouched
                onBlurCapture={handleInvoiceNumberBlur}
              />

              <Field name="invoice_date" required>
                {({ field }: FieldProps) => (
                  <LockedDatePicker
                    customSize="regular"
                    wrapperClassName="t-flex t-w-full"
                    {...field}
                    required
                    saveFormat={YYYY_MM_DD}
                    label="Invoice date"
                    placeholder={DISPLAY_DATE}
                    onDateChange={() => setFieldValue("due_date", null)}
                  />
                )}
              </Field>

              <DueDate
                name="due_date"
                size="regular"
                minDate={values.invoice_date || invoice?.invoice_date}
                required={Boolean(customer?.is_reminders_enabled)}
                dueDate={dueDate}
                options={options}
                disabled={!invoiceDate}
              />

              <Combobox
                required
                isClearable={false}
                size="regular"
                menuPortalTarget={document.body}
                label="Preferred currency"
                name="currency"
                placeholder="Select currency"
                options={currencyOptions}
                value={currentCurrency || null}
                onChange={handleCurrencyChange}
              />

              <Input
                withForm
                name="po_number"
                label="PO number"
                placeholder="Enter PO number"
              />

              <Field name="po_issue_date">
                {({ field }: FieldProps) => (
                  <DateInput
                    customSize="regular"
                    wrapperClassName="t-flex t-w-full"
                    {...field}
                    saveFormat={YYYY_MM_DD}
                    label="PO Issue date"
                    placeholder={DISPLAY_DATE}
                  />
                )}
              </Field>
            </div>
          </InvoiceFormSection>

          <InvoiceFormSection title="Item details">
            {(invoiceItems?.length ?? 0) > 0 && (
              <LineItemTable
                invoice={isSentInvoiceEditFlow ? (values as Invoice) : invoice!}
                handleItemDelete={handleItemDelete}
                handleItemEdit={handleItemEdit}
                handleItemDuplicate={handleItemDuplicate}
                invoiceItem={invoiceItem}
                isUpdatingTableItem={isUpdating}
                rootCategoryIdentifiers={[
                  REVENUE_CATEGORY_IDENTIFIER,
                  LIABILITY_CATEGORY_IDENTIFIER,
                ]}
                onBulkCategoryUpdate={handleUpdate}
                writePermissionKey={WRITE_PERMISSION_KEYS.REVENUE}
                renderEditForm={(item, index) => (
                  <LineItemUI
                    invoice_item={{ ...item, is_new_item: false }}
                    groupId={group?.uuid!}
                    entityId={entityId}
                    currency={currency}
                    closeModal={handleCloseEdit}
                    onSave={(UpdatedItem) => handleSaveItem(UpdatedItem, index)}
                    isUpdating={isUpdating}
                    rootCategoryIdentifiers={[
                      REVENUE_CATEGORY_IDENTIFIER,
                      LIABILITY_CATEGORY_IDENTIFIER,
                    ]}
                    customerId={customer?.customer_id}
                    invoiceDate={values.invoice_date || invoice?.invoice_date}
                    showTaxField={invoice?.has_item_level_tax}
                    sellerAddress={invoiceSettings?.address}
                    buyerAddress={customer?.billing_address}
                    onEditCustomerAddress={setCustomerId}
                  />
                )}
              />
            )}
            <FocusBlock.Item
              placement={FOCUS_BLOCK_PLACEMENT.INSERT}
              value="add-line-item"
            >
              <FocusBlock.Content>
                <LineItemUI
                  invoice_item={invoiceItem!}
                  groupId={group?.uuid!}
                  entityId={entityId}
                  currency={currency}
                  closeModal={handleCloseEdit}
                  onSave={handleSaveItem}
                  isUpdating={isUpdating}
                  rootCategoryIdentifiers={[
                    REVENUE_CATEGORY_IDENTIFIER,
                    LIABILITY_CATEGORY_IDENTIFIER,
                  ]}
                  customerId={customer?.customer_id}
                  invoiceDate={invoiceDate}
                  showTaxField={invoice?.has_item_level_tax}
                  sellerAddress={invoiceSettings?.address}
                  buyerAddress={customer?.billing_address}
                  onEditCustomerAddress={setCustomerId}
                />
              </FocusBlock.Content>
              <div>
                <AddLineItemButton
                  size="regular"
                  writePermissionKey={WRITE_PERMISSION_KEYS.REVENUE}
                  onClick={handleAddLineItem}
                />
              </div>
            </FocusBlock.Item>
          </InvoiceFormSection>

          <InvoiceFormSection title="Discount">
            <InvoiceDiscountFields
              hasItemLevelTax={invoice?.has_item_level_tax}
            />
          </InvoiceFormSection>

          {hasCreditNotes && (
            <InvoiceFormSection
              title={
                <div className="t-flex t-gap-2 t-items-center">
                  Credit Notes
                  <ToolTip
                    align="start"
                    alignOffset={-10}
                    keepOpenOnActivation
                    text={
                      <div>
                        Issue a Credit Note to adjust advance payments and
                        overpayments
                      </div>
                    }
                  >
                    <span className="t-text-text-30 t-flex t-items-center">
                      <Info color="currentColor" size="12" />
                    </span>
                  </ToolTip>
                </div>
              }
            >
              <CreditNotesTable
                creditNotes={invoice?.linked_credit_debit_notes || []}
              />
            </InvoiceFormSection>
          )}

          {invoice && invoiceItems && invoiceItems.length > 0 && (
            <div>
              <Divider type="dashed" color="dark-grey" />
              <div className="t-flex t-gap-3 t-flex-col t-mt-6">
                {currency !== USD && (
                  <FocusBlock.Item
                    placement={FOCUS_BLOCK_PLACEMENT.REPLACE}
                    value="set-exchange-rate"
                  >
                    <FocusBlock.Content>
                      <ExchangeRateContent
                        invoiceId={invoiceId}
                        currency={values.currency}
                        currentRate={exchangeRate}
                        originalRate={originalRate}
                      />
                    </FocusBlock.Content>
                    <Banner
                      type="info"
                      description={
                        <div className="t-flex t-flex-col t-gap-1">
                          <div className="t-flex t-items-center t-gap-2">
                            <span>{exchangeRateBannerDescription}</span>
                            {invoice.has_exchange_rate_changed && (
                              <Tag
                                tagType="white"
                                icon={false}
                                size="small"
                                rounded
                                bordered
                              >
                                Custom
                              </Tag>
                            )}
                          </div>
                          {isFutureInvoiceDate && (
                            <span className="t-text-body-sm-lite t-text-text-30">
                              Future invoice dates requires custom rate setting
                            </span>
                          )}
                        </div>
                      }
                      icon={
                        ((isUpdatedRate && isFutureInvoiceDate) ||
                          !isFutureInvoiceDate) && (
                          <span
                            className={classNames({
                              "-t-mt-6": isUpdatedRate && isFutureInvoiceDate,
                              "-t-mt-1": !isFutureInvoiceDate,
                            })}
                          >
                            <Info
                              color={colors.blue[80]}
                              stroke="1.2"
                              size="18"
                            />
                          </span>
                        )
                      }
                      cta={
                        <FocusBlock.Trigger asChild>
                          <Button
                            customType="link"
                            size={isFutureInvoiceDate ? "regular" : "small"}
                          >
                            {invoice.has_exchange_rate_changed
                              ? "Edit custom rate"
                              : "Set custom rate"}
                          </Button>
                        </FocusBlock.Trigger>
                      }
                    />
                  </FocusBlock.Item>
                )}

                <div className="t-flex t-justify-between t-items-center">
                  <span className="t-text-body-sm">Total</span>
                  <span className="t-text-subtext">
                    <AmountSuperScript
                      amount={Number(invoice.due_balance)}
                      currencyCode={currency}
                    />
                  </span>
                </div>

                {currency !== USD && (
                  <div className="t-flex t-justify-between t-items-center">
                    <span className="t-text-body-sm t-text-text-30">
                      USD Equivalent
                    </span>
                    <span className="t-text-subtext">
                      {exchangeRate ? (
                        <AmountSuperScript
                          amount={
                            Number(invoice.due_balance) / Number(exchangeRate)
                          }
                        />
                      ) : (
                        "..."
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <InvoiceFormSection title="Additional details">
            <InvoiceAdditionalDetails />
          </InvoiceFormSection>

          {invoice && (
            <InvoiceFormSection title="Attachments">
              <InvoiceAttachments invoice={invoice} />
            </InvoiceFormSection>
          )}
        </div>
      </FocusBlock.Root>
      <InvoiceCustomerModal
        customerId={customerId}
        setCustomerId={setCustomerId}
        onCustomerCreated={(newCustomer) => {
          setFieldValue("customer_id", newCustomer.customer_id);
          setFieldValue("customer", newCustomer);
          setFieldValue("currency", newCustomer.currency);
        }}
      />

      {/* Currency Change Confirmation Modal */}
      <Modal.Root
        open={currencyModal.isOpen}
        onOpenChange={currencyModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Change Currency</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <p className="t-mb-0 t-text-body-sm t-text-text-60">
              This action will update the invoice currency & adjust the dollar
              amount based on the exchange rate.
              <br />
              Are you sure you want to continue?
            </p>
          </Modal.Body>

          <Modal.FooterButtonGroup>
            <Button customType="secondary" onClick={currencyModal.close}>
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={() => {
                if (newCurrency) {
                  setFieldValue("currency", newCurrency);
                }
                currencyModal.close();
              }}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};
